import * as React from 'react';
import { Box, Typography, Button, useTheme, Fade, Paper } from '@mui/material';
import {
  Loader2,
  CheckCircle2,
  Rocket,
  Sparkles,
  AlertCircle,
} from 'lucide-mui';
import type { FileType } from '../../types';
import UpgradeModal from '../plan/upgrade/UpgradeModal';

type FileProcessingStatus =
  | 'pending'
  | 'transcribing'
  | 'transcribed'
  | 'diarizing'
  | 'processing-templates'
  | 'completed'
  | 'limit-reached'
  | 'no-audio';

interface StatusProps {
  file: FileType;
  postProcess?: {
    systemPromptResponses?: Record<string, any>;
  };
}

const ICON_STYLE = {
  width: 20,
  height: 20,
  strokeWidth: 2,
};

const LOADER_STYLE = {
  width: 24,
  height: 24,
  strokeWidth: 2,
};

const Status: React.FC<StatusProps> = ({ file, postProcess }) => {
  const [
    fileProcessingStatus,
    setFileProcessingStatus,
  ] = React.useState<FileProcessingStatus>('pending');
  const [showStatus, setShowStatus] = React.useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const theme = useTheme();

  React.useEffect(() => {
    const getFileProcessingStatus = (): FileProcessingStatus => {
      const hasAudio = file.streams?.includes('audio');
      if (!hasAudio) {
        return 'no-audio';
      }
      const transciptionComplete = !!file.textData?.transcriptCompletedAt;
      const diarizationComplete = !!file.textData?.speakers;
      const templateProcessingComplete = !!Object.keys(
        postProcess?.systemPromptResponses || {}
      ).length;
      if (!transciptionComplete) {
        return 'transcribing';
      }
      if (transciptionComplete && !diarizationComplete) {
        return 'transcribed';
      }
      if (
        transciptionComplete &&
        diarizationComplete &&
        !templateProcessingComplete
      ) {
        return 'processing-templates';
      }
      if (
        transciptionComplete &&
        diarizationComplete &&
        templateProcessingComplete
      ) {
        return 'completed';
      }
      return 'pending';
    };

    if (file) {
      const newStatus = getFileProcessingStatus();
      setFileProcessingStatus(newStatus);

      // Only show after a delay if it's not completed
      if (newStatus !== 'completed') {
        const timer = setTimeout(() => {
          setShowStatus(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
      setShowStatus(false);
      return undefined;
    }
    return undefined;
  }, [file, postProcess]);

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  if (fileProcessingStatus === 'completed' || !showStatus) {
    return null;
  }

  const getStatusContent = () => {
    switch (fileProcessingStatus) {
      case 'no-audio':
        return {
          message: 'No Audio Track Available',
          subMessage:
            'You can still analyze the video content and ask questions about what you see',
          color: theme.palette.text.secondary,
          icon: (
            <AlertCircle
              sx={{
                ...ICON_STYLE,
                color: theme.palette.text.secondary,
              }}
            />
          ),
        };
      case 'pending':
        return {
          message: 'Preparing to Transcribe...',
          subMessage: 'This will take a moment',
          color: theme.palette.info.main,
          icon: (
            <Loader2
              sx={{
                ...LOADER_STYLE,
                animation: 'spin 2s linear infinite',
                color: theme.palette.info.main,
              }}
            />
          ),
        };
      case 'transcribing':
        return {
          message: 'Transcribing...',
          subMessage:
            'Converting speech to text. This may take a minute or two',
          color: theme.palette.primary.main,
          icon: (
            <Loader2
              sx={{
                ...LOADER_STYLE,
                animation: 'spin 2s linear infinite',
                color: theme.palette.primary.main,
              }}
            />
          ),
        };
      case 'transcribed':
        return {
          message: 'Transcription Ready',
          subMessage:
            'Your transcription is ready for searching, asking questions, and generating insights',
          color: theme.palette.success.main,
          icon: (
            <CheckCircle2
              sx={{
                ...ICON_STYLE,
                animation: 'fadeIn 0.5s ease-in',
                color: theme.palette.success.main,
              }}
            />
          ),
        };
      case 'diarizing':
      case 'processing-templates':
        return {
          message: 'Enhancing your Transcription...',
          subMessage:
            'Identifying speakers and improving summaries. This may take a minute or two',
          color: theme.palette.primary.main,
          icon: (
            <Sparkles
              sx={{
                ...ICON_STYLE,
                animation: 'pulse 2s ease-in-out infinite',
                color: theme.palette.primary.main,
              }}
            />
          ),
        };
      case 'limit-reached':
        return {
          message: 'You have reached your transcription limit',
          subMessage: 'Upgrade your plan to increase your limit.',
          color: theme.palette.warning.main,
          icon: (
            <Rocket sx={{ ...ICON_STYLE, color: theme.palette.warning.main }} />
          ),
          action: (
            <Button
              onClick={handleUpgradeClick}
              variant="contained"
              size="small"
              color="primary"
              sx={{
                ml: 2,
                px: 2,
                py: 0.75,
                fontWeight: 600,
                textTransform: 'none',
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Upgrade Now
            </Button>
          ),
        };
      default:
        return null;
    }
  };

  const statusContent = getStatusContent();
  if (!statusContent) return null;

  return (
    <>
      <Fade in={showStatus} timeout={500}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
            px: 3,
            mb: 2,
            mt: 3,
            mx: { xs: 2, sm: 0 },
            borderRadius: 2,
            backgroundColor: `${statusContent.color}15`,
            transition: 'all 0.3s ease-in-out',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
            '@keyframes pulse': {
              '0%': { opacity: 0.6, transform: 'scale(0.98)' },
              '50%': { opacity: 1, transform: 'scale(1.02)' },
              '100%': { opacity: 0.6, transform: 'scale(0.98)' },
            },
            '@keyframes sparkle': {
              '0%': { opacity: 0.7, transform: 'scale(1) rotate(0deg)' },
              '50%': { opacity: 1, transform: 'scale(1.1) rotate(180deg)' },
              '100%': { opacity: 0.7, transform: 'scale(1) rotate(360deg)' },
            },
            '@keyframes bounce': {
              '0%': { transform: 'translateX(0)' },
              '50%': { transform: 'translateX(3px)' },
              '100%': { transform: 'translateX(0)' },
            },
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'scale(0.9)' },
              '100%': { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              color: statusContent.color,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {statusContent.icon}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 0.25,
                  }}
                >
                  {statusContent.message}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    display: 'block',
                  }}
                >
                  {statusContent.subMessage}
                </Typography>
              </Box>
            </Box>
            {statusContent.action}
          </Box>
        </Paper>
      </Fade>
      {showUpgradeModal && <UpgradeModal onClose={handleCloseUpgradeModal} />}
    </>
  );
};

export default Status;
